import React, { useEffect, useState, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { FileDown } from "lucide-react";
import useSurveyManagementStore from "../stores/surveyManagementStore";

const BudgetReport = ({ id }) => {
  const [loading, setLoading] = useState(true);
  const [costs, setCosts] = useState({
    "Potholes": 125,
    "Raveling road": 125,
    "Alligator Cracks": 125,
    "Block Cracks": 125,
    "Longitudinal Cracks": 125,
    "Transverse cracks": 125,
    "Edge Cracks": 125,
    "Reflection Cracks": 125,
    "Rutting": 125,
    "Faulting": 125,
  });
  const [tempCosts, setTempCosts] = useState({
    "Potholes": "125",
    "Raveling road": "125",
    "Alligator Cracks": "125",
    "Block Cracks": "125",
    "Longitudinal Cracks": "125",
    "Transverse cracks": "125",
    "Edge Cracks": "125",
    "Reflection Cracks": "125",
    "Rutting": "125",
    "Faulting": "125",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const {
    budgetReport,
    budgetReportById,
    downloadBudgetReportById,
    fetchCurrencies,
    currencies,
    selectedCurrency,
    setCurrency,
  } = useSurveyManagementStore();

  // Fetch currencies and budget report on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        await fetchCurrencies();
        await budgetReport(id, costs);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchInitialData();
    }
  }, [id, budgetReport, fetchCurrencies]);

  // Refetch budget report when currency changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await budgetReport(id, costs); // Pass the costs to the budgetReport backend function
      } catch (error) {
        console.error("Error fetching budget report:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [selectedCurrency, budgetReport, id]);

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm(""); // Reset search term when closing dropdown
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleTempCostChange = (defect, value) => {
    setTempCosts((prev) => ({
      ...prev,
      [defect]: value,
    }));
  };

  const handleCostUpdate = async (defect) => {
    const newCost = parseFloat(tempCosts[defect]) || 0;
    setCosts((prev) => ({
      ...prev,
      [defect]: newCost,
    }));
    try {
      setLoading(true);
      const updatedCosts = { ...costs, [defect]: newCost };
      await budgetReport(id, updatedCosts);
    } catch (error) {
      console.error("Error fetching budget report:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (defect, e) => {
    if (e.key === "Enter") {
      handleCostUpdate(defect);
    }
  };

  const handleCurrencySelect = (currencyCode) => {
    setCurrency(currencyCode);
    setSearchTerm("");
    setIsDropdownOpen(false);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCurrencyFieldClick = () => {
    setIsDropdownOpen(true);
  };

  const handleDownload = async (id) => {
    try {
      await downloadBudgetReportById(id, costs);
      console.log("Download initiated with costs:", costs);
    } catch (error) {
      console.error("Error downloading report:", error);
    }
  };

  // Filter currencies based on search term
  const filteredCurrencies = currencies.filter(
    (currency) =>
      currency.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      currency.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get the display text for the selected currency
  const selectedCurrencyDisplay = useMemo(() => {
    const currency = currencies.find((c) => c.code === selectedCurrency);
    return currency ? `${currency.code} - ${currency.name}` : selectedCurrency || "Select Currency";
  }, [selectedCurrency, currencies]);

  // Update defect costs with the latest costs state
  const updatedDefects = useMemo(() => {
    if (!budgetReportById?.defects) return [];
    return budgetReportById.defects.map((defect) => ({
      ...defect,
      cost: costs[defect.name] || defect.cost || 0,
    }));
  }, [budgetReportById?.defects, costs]);

  // Memoize column definitions with dynamic currency
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Defects",
        field: "name",
        flex: 2.5,
        checkboxSelection: true,
        headerCheckboxSelection: true,
      },
      {
        headerName: `Cost (${selectedCurrency})`,
        flex: 2,
        field: "cost",
        valueFormatter: (params) => params.value?.toLocaleString() || "0",
      },
      {
        headerName: "Found",
        flex: 1,
        field: "found",
        valueFormatter: (params) => params.value?.toLocaleString() || "0",
      },
      {
        headerName: "Total",
        flex: 1,
        valueGetter: (params) =>
          (params.data?.cost || 0) * (params.data?.found || 0),
        valueFormatter: (params) => params.value?.toLocaleString() || "0",
      },
    ],
    [selectedCurrency]
  );

  // Memoize default column definitions
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
      floatingFilter: true,
      suppressMenu: true,
    }),
    []
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const totalLength = budgetReportById?.totalLength?.toLocaleString() || "0";
  const totalBudget =
    budgetReportById?.totalEstimatedBudget?.toLocaleString() || "0";

  return (
    <div className="flex gap-4 p-4">
      {/* Sidebar */}
      <div className="w-1/3 space-y-4">
        {/* Currency Selection with Searchable Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 w-1/3">Currency</label>
            <div
              onClick={handleCurrencyFieldClick}
              className="w-2/3 p-2 border rounded bg-white cursor-pointer flex items-center justify-between"
            >
              <span className="truncate">{selectedCurrencyDisplay}</span>
              <svg
                className={`w-4 h-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
          {isDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-60 flex flex-col">
              {/* Search Input Inside Dropdown */}
              <div className="p-2 border-b">
                <input
                  type="text"
                  placeholder="Search currency..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full p-1 border rounded text-sm focus:outline-none"
                  autoFocus
                />
              </div>
              {/* Currency List */}
              <div className="overflow-y-auto flex-1">
                {filteredCurrencies.length > 0 ? (
                  filteredCurrencies.map((currency) => (
                    <div
                      key={currency.code}
                      onClick={() => handleCurrencySelect(currency.code)}
                      className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                    >
                      {currency.code} - {currency.name}
                    </div>
                  ))
                ) : (
                  <div className="p-2 text-gray-500 text-sm">
                    {searchTerm
                      ? "No currencies found"
                      : "Loading currencies..."}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="space-y-2">
          {Object.keys(costs).map((defect) => (
            <div key={defect} className="flex items-center gap-2">
              <label className="text-sm text-gray-600 w-1/2">{defect}</label>
              <input
                type="number"
                value={tempCosts[defect]}
                onChange={(e) => handleTempCostChange(defect, e.target.value)}
                onKeyPress={(e) => handleKeyPress(defect, e)}
                onBlur={() => handleCostUpdate(defect)}
                className="w-1/2 p-1 border rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                min="0"
                step="0.01"
              />
            </div>
          ))}
        </div>
        <div className="flex gap-4">
          <div className="p-4 border border-[#1a539c] rounded-lg text-center w-1/2">
            <p className="text-sm text-gray-600">Total Length</p>
            <p className="text-2xl font-bold text-[#1a539c]">{totalLength}</p>
          </div>
          <div className="p-4 bg-[#1a539c] text-white rounded-lg text-center w-1/2">
            <p className="text-sm">
              Total Estimated Budget ({selectedCurrency})
            </p>
            <p className="text-2xl font-bold">{totalBudget}</p>
          </div>
        </div>
        <button
          className="mt-6 bg-[#a9e5f7] px-4 py-4 justify-center rounded-md flex items-center gap-2 text-[#074659] font-bold w-full"
          onClick={() => handleDownload(id)}
        >
          <FileDown size={20} />
          DOWNLOAD REPORT
        </button>
      </div>

      {/* AG Grid Table */}
      <div
        className="ag-theme-alpine w-2/3 border border-gray-400 rounded-lg"
        style={{
          height: "calc(100vh - 200px)",
          "--ag-row-hover-color": "#afb3b6",
          "--ag-borders": "none",
          "--ag-row-height": "60px",
          "--ag-header-height": "50px",
          "--ag-cell-horizontal-padding": "15px",
        }}
      >
        <AgGridReact
          rowData={updatedDefects}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowHeight={40}
          domLayout="autoHeight"
          animateRows={true}
          rowSelection="multiple"
        />
        <div className="bg-gray-600 mt-1 text-white p-4 flex justify-between">
          <span className="text-2xl">
            Total Budget ({selectedCurrency})
          </span>
          <span className="text-2xl">{totalBudget}</span>
        </div>
      </div>
    </div>
  );
};

export default BudgetReport;
